import { useCallback, useEffect, useState } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom'

import MobileTopBar from './MobileTopBar'
import SearchModal, { OPEN_SEARCH_EVENT } from './SearchModal'

export default function Layout() {
  // Drawer state only applies on mobile — desktop CSS keeps the sidebar
  // visible always. SSR / first render start closed to avoid a flash.
  const [drawerOpen, setDrawerOpen] = useState(false)
  const openDrawer = useCallback(() => setDrawerOpen(true), [])
  const closeDrawer = useCallback(() => setDrawerOpen(false), [])

  const location = useLocation()
  useEffect(() => {
    setDrawerOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (!drawerOpen) return
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') setDrawerOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [drawerOpen])

  useEffect(() => {
    if (!drawerOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [drawerOpen])

  // Render the platform-aware "⌘K / Ctrl K" hint on the client only to avoid
  // a hydration mismatch.
  const [isMac, setIsMac] = useState(false)
  useEffect(() => {
    setIsMac(/Mac|iP(hone|ad|od)/.test(navigator.platform))
  }, [])

  return (
    <div className="docs-shell">
      <MobileTopBar onMenuClick={openDrawer} />
      <aside
        className={drawerOpen ? 'docs-sidebar is-open' : 'docs-sidebar'}
        aria-label="API documentation navigation"
      >
        <div className="brand">
          <span className="brand-dot" aria-hidden />
          <Link to="/" onClick={closeDrawer}>
            Chosen API
          </Link>
        </div>

        <button
          type="button"
          className="search-trigger"
          onClick={() => {
            closeDrawer()
            window.dispatchEvent(new Event(OPEN_SEARCH_EVENT))
          }}
        >
          <span>Search the API docs</span>
          <kbd>{isMac ? '⌘' : 'Ctrl'} K</kbd>
        </button>

        <nav>
          <div className="nav-group">
            <p className="nav-group-title">Get started</p>
            <NavLink to="/" end onClick={closeDrawer} className={navClass}>
              Overview
            </NavLink>
            <NavLink to="/reference" onClick={closeDrawer} className={navClass}>
              API reference
            </NavLink>
          </div>

          <div className="nav-group">
            <p className="nav-group-title">Guides</p>
            <NavLink
              to="/guides/authentication"
              onClick={closeDrawer}
              className={navClass}
            >
              Authentication
            </NavLink>
            <NavLink
              to="/guides/errors"
              onClick={closeDrawer}
              className={navClass}
            >
              Errors
            </NavLink>
            <NavLink
              to="/guides/rate-limits"
              onClick={closeDrawer}
              className={navClass}
            >
              Rate limits
            </NavLink>
            <NavLink
              to="/guides/versioning"
              onClick={closeDrawer}
              className={navClass}
            >
              Versioning
            </NavLink>
            <NavLink
              to="/guides/public-endpoints"
              onClick={closeDrawer}
              className={navClass}
            >
              Unauth public endpoints
            </NavLink>
          </div>

          <div className="nav-group">
            <p className="nav-group-title">Manage</p>
            <a
              href="https://hire.chosenhq.com/app/settings/api-keys"
              className="nav-link"
            >
              Get a key →
            </a>
          </div>
        </nav>
      </aside>

      <div
        className={drawerOpen ? 'docs-scrim is-visible' : 'docs-scrim'}
        onClick={closeDrawer}
        aria-hidden
      />

      <main className="docs-content">
        <div className="docs-content-inner">
          <div className="beta-banner">
            <strong>Beta.</strong> The public API is stabilizing — we may make
            non-breaking additions, but breaking changes will be released as
            v2. Subscribe to <code>changelog@chosenhq.com</code> for updates.
          </div>
          <Outlet />
        </div>
      </main>

      <SearchModal />
    </div>
  )
}

function navClass({ isActive }: { isActive: boolean }): string {
  return isActive ? 'nav-link active' : 'nav-link'
}
